/**
  * @class meleWfTable
  * @description
  *
  * # Purpose
  * Implements `<mele-wftable>` custom web element.
  * Displays table with rows as objects from external workflow
  *
  */

import meleElement from 'meleElement'
import msysWorkflow from 'msysWorkflow'
const msysListener = require('msysListener')

const PROPERTIES = _.extend({}, {
  server: { description: 'workflow server', type: 'string' },
  new: { description: 'whether to show new button', type: 'boolean' },
  query: { description: 'watch query', type: 'string' },
  fields: { description: 'watch fields', type: 'string' },
  sort: { description: 'sort criteria', type: 'string' },
  actions: { description: 'whether to display available actions', type: 'boolean' },
  click: { description: "what to do on click; 'link:<module>' or 'open' or 'actions'", type: 'string' },
}, meleElement._properties)

export default class meleWfTable extends meleElement {
  init() {
    super.init()
    this.data_query = null
    this.data_version = 0
    this.listener = new msysListener.default(0)
    this.listener.publish('#ME', 'mmStart')
    this.listener.mmObjects = this.mmObjects.bind(this)
    this.listener.mmModel = this.mmModel.bind(this)
    this.recompute = 'update()'
    this.elNewButtons = this.appendChildren({
      tag: 'div',
      class: 'newbuttons',
    })
  }

  newChanged() {
    this.elNewButtons.innerHTML = ''
    if(!this.new) return

    Object.keys(_.get(this, 'workflow.states..actions', {})).forEach(new_action=>{
      const record = { id: '', state: '' }
      this.elNewButtons.appendChildren({
        tag: 'mele-button',
        type: 'actionbar',
        iconleft: 'sys.plus',
        label: 'wf.'+_.get(this, 'workflow.id')+'.action.'+new_action,
        class: 'noreadonly',
        eventButtonClicked: ()=>{
          const action_form_name = this.workflow.formForAction(record, new_action)
          const elPopup = this.openActionPopup(record, action_form_name, new_action)
          elPopup.updateValues({ building_id: this.closest('.mele-container:not([propagate])').getValue('id') })
        },
      })
    })
  }

  update(holder) {
    let query = this.query || '{}'
    query = this.translate(query, false, 'query')
    if(query != this.data_query) {
      this.data_version = 0
      this.data_query = query
    }
    const params = {
      // version: this.data_version,
      version: 0,
      actions: true,
    }
    try{ params.query = JSON.parse(query) }catch(e) {}
    try{ params.sort = JSON.parse(this.sort) }catch(e) {}
    try{ params.fields = JSON.parse(this.fields) }catch(e) {}
    params.fields = _.union(['id', 'state'], params.fields)
    this.data = null
    this.listener.publish(':'+this.server, 'mmModelGet', { md5: this.workflow ? this.workflow.md5 : '' })
    this.listener.publish(':'+this.server, 'mmWatch', params)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * workflow model definition
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id] - workflow ID
  * @param {integer} [params.version] - model version
  * @param {string} [params.icon] - SVG icon
  * @param {string} [params.md5] - MD5 checksum
  * @param {string} [params.id_generator] - indicates who and how generates id
  * @param {string} [params.sqlview] - definition of SQL query to retrieve read-only workflow
  * @param {string} [params.sqlview_log] - definition of SQL query to retrieve read-only workflow logs
  * @param {string} [params.notifications] - indicates whether to notify on object changes
  * @param {object} [params.fields] - workflow variables
  * @param {object} [params.sql_queries] - SQL queries used for calculation of fields
  * @param {object} [params.forms] - workflow GUI forms
  * @param {string} [params.translations] - workflow translations as CSV
  * @param {string} [params.scripts] - workflow scripts
  * @param {string} [params.styles] - workflow CSS styles
  * @param {array} [params.acls] - access control list
  * @param {object} [params.states] - workflow states and their actions
  */
  mmModel(sender, params) {
    if(params.states) {
      this.workflow = new msysWorkflow(this, params)
      try{
        var field_names = JSON.parse(this.fields)
      }catch(e) {
        field_names = Object.keys(this.workflow.fields)
      }
      const form = { columns: field_names }
      msysWorkflow.fillFormDefaults('', form, this.workflow.id, this.workflow.forms, this.workflow.fields)
      this.columns = form.columns
      this.workflow.md5 = params.md5
    }
    this.redraw()
    this.newChanged()
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * used to return list of workflow processes
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - indicates if data were limited to some fields only
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.sort] - sort criteria
  * @param {object} [params.query] - query parameters
  * @param {string} [params.type] - type of returned objects, e.g. id of workflow producing the objects
  * @param {integer} [params.total] - total number of objects matching the query
  * @param {integer} [params.offset] - offset of returned objects
  * @param {integer} [params.limit] - limits used in the query
  * @param {array} params.list - array of matching objects
  */
  mmObjects(sender, params) {
    this.data_version = params.version
    this.data = params.list
    this.redraw()
  }

  redraw() {
    if(!this.workflow || !this.data) return
    this.forAll('table', el=>el.deleteElement())

    const elTable = this.appendChildren({
      tag: 'table',
    })
    const elHeader = elTable.appendChildren({ tag: 'tr' })
    this.columns.forEach(col=>{
      elHeader.appendChildren({ tag: 'th', html: this.translate(col.label, false) })
    })

    this.data.forEach(item=>{
      const elRow = elTable.appendChildren({ tag: 'tr' })
      this.columns.forEach(col=>{
        // translate dependent column
        let prefix = _.toString(col.prefix)
        prefix = prefix.replace(/@{([^}]+)}/g, (m, key)=>item[key])

        // calculate value
        let value = prefix + _.toString(item[col.key])
        if(prefix!='' || col.localize) value = this.translate(value, true)

        // create table cell
        const elCell = elRow.appendChildren({ tag: 'td', html: value })
        const me = this
        elCell.onclick = function() { me.onCellClick(this, item.id, col.key) }
      })
    })
  }

  tr(phrase) {
    phrase = _.toString(phrase)
    if(phrase=='') return ''
    return 'wf.'+_.get(this, 'workflow.id', 'generic')+'.'+phrase
  } // tr

  onCellClick(cell, id, column) {
    const tr = cell.closest('tr')
    tr.closest('table').forAll('tr', el=>el.classList.remove('selected'))
    tr.classList.add('selected')
    if(/^link:/.test(this.click)) {
      const mod = this.click.substr(5)
      this.listener.publish('msysWM', 'mmWindowFocus', { id: mod })
      this.listener.publish(mod, 'mmOpen', { id })
      return
    }

    const item = this.data.filter(x=>x.id==id)[0]
    if(!item) return

    if(this.click=='actions') {
      const buttons = this.buttonsForState(item.state, item._actions)
      if(buttons.length==0) return
      if(buttons.length==1) {
        const action_name = buttons[0].key.replace(/^ACTION:/, '')
        const action_form = this.workflow.formForAction(item, action_name)
        if(action_form===null) return // no definition
        if(action_form) return this.openActionPopup(item, action_form, action_name)
        // otherwise continue, i.e. display list of possible actions with just one action to choose from
        // this is useful as a form of confirmation for the user
      }
      tr.modalOpen({
        position: 'below',
        outclick: ':modal.cancel',
        class: 'modal-wf-actions',
        items: buttons,
      })
    }else if(this.click=='open') {
      this.openActionPopup(item, item._form, item._actions)
    }
  }

  buttonsForState(state, desired_actions) {
    const buttons = []
    const state_actions = _.get(this, 'workflow.states.'+state+'.actions')
    if(!state_actions) return []
    for(const action_name in state_actions) {
      if(desired_actions && desired_actions.indexOf(action_name)<0) continue // possible action not desired
      const action = state_actions[action_name]
      buttons.push({
        tag: 'mele-button',
        type: 'actionbar',
        key: 'ACTION:'+action_name,
        color: action.color || '',
        action: 'submitvalid',
        class: 'noreadonly color-'+(action.color || ''),
        label: this.tr('action.'+action_name),
      })
    }
    return buttons
  } // buttonsForState

  openActionPopup(record, form_name, desired_actions) {
    this.closeActionPopup()
    let form = this.workflow.forms[form_name]
    if(!record || !form) return

    form = _.extend({}, form, {
      tag: 'mele-popup',
      view: true,
      class: 'popupdialog wf '+this.workflow.id,
      position: 'center',
      outclick: ':modal.cancel',
      eventCancel: ()=>{
        if(!this.elPopup.changed) {
          this.elPopup.popupClose()
          delete this.elPopup
        } else {
          const elYesno = this.popupConfirm(this.tr('AreYouSureToDiscardChanges'), ()=>{
            elYesno.popupClose()
            if(this.elPopup) this.elPopup.popupClose()
            delete this.elPopup
          })
        }
      },
      eventSubmit: this.submitAction.bind(this),
    })

    if(!form.headerbuttons) form.headerbuttons = ['modalx']
    if(!form.footerbuttons) {
      form.footerbuttons = this.buttonsForState(record.state, desired_actions)
      form.footerbuttons.unshift(form.footerbuttons.length>0 ? 'popupcancel' : 'popupclose')
    }

    // this.formHook('formopen', record, form_name)
    const elPopup = this.elPopup = this.popupOpen(form)
    elPopup.datakey = record.id
    // this.formHook('formload', record, elPopup)
    elPopup.setValues(record)
    elPopup.commitValues()
    // this.updateLogTable(elPopup, record._logs)
    // this.updateComputed(elPopup)
    // this.formHook('formready', record, elPopup)
    if(elPopup && elPopup._body && typeof elPopup._body.scrollIntoView === 'function') {
      setTimeout(()=>{
        elPopup._body.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 50)
    }

    if(record.id && record._limited) {
      // object not fully loaded, request full details
      this.serverRequest('LoadingObject', 'mmGet', {
        version: 1, // this is to prevent return of "full" response
        query: { id: record.id },
        actions: true,
        logs: true,
      })
    }

    return elPopup
  } // openActionPopup

  closeActionPopup() {
    if(this.elPopup) {
      this.elPopup.deleteElement()
      delete this.elPopup
    }
  } // closeActionPopup

  async submitAction(form, key, values, btn) {
    const title = values.id ? 'CreatingObject' : 'UpdatingObject'
    const reply = await this.serverRequest(title, 'mmAction', {
      id: values.id,
      action: btn.key.replace('ACTION:', ''),
      params: form.getValues(true),
    })
    if(reply) this.closeActionPopup()
  } // submitAction

  async serverRequest(title, action, params) {
    title = title ? this.tr(title) : null
    if(title) this.popupWait(this.tr('PleaseWait'), title)
    try{
      const msg = await this.listener.request(':'+this.server, action, params, ['mmSuccess', 'mmObjects'])
      this.popupCloseDefault()
      if(msg.action=='mmObjects') this.mmObjects(msg.sender, msg.params, params)
      return msg
    }catch(e) {
      this.popupError(String(e), title)
    }
  } // serverRequest

  newObject() {
  }
} // class meleWfTable

meleWfTable._properties = PROPERTIES
meleWfTable.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-wftable', meleWfTable)
